(() => {
  const rootSelector = '[data-lc-premium-product]';

  function integer(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.round(parsed) : fallback;
  }

  function pulse(element, className = 'lc-selection-pulse', duration = 1700) {
    if (!element || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);
    window.setTimeout(() => element.classList.remove(className), duration);
  }

  function availableTierRadios(section) {
    return Array.from(
      section.querySelectorAll('quantity-breaks input[type="radio"][name="quantity"]')
    ).filter((radio) => !radio.disabled && !radio.closest('.hidden'));
  }

  function selectedTierTotalCents(section) {
    const quantityBreaks = section.querySelector('quantity-breaks');
    const selected = availableTierRadios(section).find((radio) => radio.checked);
    const card = selected?.nextElementSibling;
    if (!quantityBreaks || !selected || !card) return 0;

    const offerNumber = selected.dataset.input?.replace('input_', '');
    const liveOfferPrice = quantityBreaks.offerPrices?.[`offer_${offerNumber}`]?.price;
    if (Number.isFinite(Number(liveOfferPrice))) {
      const subscriptionPrice =
        typeof quantityBreaks.applySubTo === 'function'
          ? quantityBreaks.applySubTo(liveOfferPrice)
          : liveOfferPrice;
      return Math.max(0, integer(subscriptionPrice));
    }

    // Initial Liquid value while Shopify Agentic Theme v1.0's custom element upgrades. Shopify Agentic Theme v1.0's live
    // offerPrices state takes over as soon as it is available.
    return Math.max(0, integer(card.dataset.tierPriceCents));
  }

  function selectedUpsellTotalCents(section) {
    return Array.from(section.querySelectorAll('.lc-mani-upsell[data-selected="true"]')).reduce(
      (total, upsell) => {
        if (Number.isFinite(Number(upsell.price))) return total + integer(upsell.price);

        const selectedVariant = upsell
          .getVariantData?.()
          ?.find(({ id }) => String(id) === String(upsell.dataset.id));
        return total + integer(selectedVariant?.price || upsell.dataset.lcPriceCents);
      },
      0
    );
  }

  function cartSubtotalCents(card) {
    const drawerItems = document.querySelector('#CartDrawer cart-drawer-items[data-subtotal]');
    return integer(drawerItems?.dataset.subtotal, integer(card.dataset.cartTotalCents));
  }

  function initialiseTierStepper(section) {
    const stepper = section.querySelector('[data-lc-tier-stepper]');
    const quantityBreaks = section.querySelector('quantity-breaks');
    if (!stepper || !quantityBreaks || stepper.dataset.initialised === 'true') return;

    const minus = stepper.querySelector('[data-lc-tier-minus]');
    const plus = stepper.querySelector('[data-lc-tier-plus]');
    const output = stepper.querySelector('[data-lc-tier-value]');
    if (!minus || !plus || !output) return;

    let selectedValue = availableTierRadios(section).find((radio) => radio.checked)?.value || '';

    const update = () => {
      const radios = availableTierRadios(section);
      const selected = radios.find((radio) => radio.checked);
      const selectedIndex = selected ? radios.indexOf(selected) : -1;
      output.textContent = selected ? selected.value : '—';
      minus.disabled = selectedIndex <= 0;
      plus.disabled = selectedIndex < 0 || selectedIndex >= radios.length - 1;
      stepper.dataset.quantity = selected ? selected.value : '';
    };

    const handleChange = () => {
      const selected = availableTierRadios(section).find((radio) => radio.checked);
      if (selected && selected.value !== selectedValue) {
        pulse(selected.nextElementSibling);
        selectedValue = selected.value;
      }
      update();
    };

    const move = (direction) => {
      const radios = availableTierRadios(section);
      const selectedIndex = radios.findIndex((radio) => radio.checked);
      const targetIndex = Math.max(0, Math.min(radios.length - 1, selectedIndex + direction));
      const target = radios[targetIndex];
      if (target && target !== radios[selectedIndex]) {
        target.click();
        target.focus({ preventScroll: true });
      }
    };

    minus.addEventListener('click', () => move(-1));
    plus.addEventListener('click', () => move(1));
    quantityBreaks.addEventListener('change', handleChange);

    const observer = new MutationObserver(update);
    observer.observe(quantityBreaks, {
      subtree: true,
      attributes: true,
      childList: true,
      characterData: true,
      attributeFilter: ['checked', 'disabled', 'class', 'data-base-price', 'data-price'],
    });

    stepper.dataset.initialised = 'true';
    update();
  }

  function initialiseUpsellAccessibility(section) {
    section.querySelectorAll('.lc-mani-upsell').forEach((upsell) => {
      if (upsell.dataset.lcA11yInitialised === 'true') return;
      const button = upsell.querySelector('.upsell-toggle-btn');
      if (!button) return;

      let selected = upsell.dataset.selected === 'true';
      const sync = () => {
        const nextSelected = upsell.dataset.selected === 'true';
        button.setAttribute('aria-pressed', nextSelected ? 'true' : 'false');
        if (nextSelected && nextSelected !== selected) pulse(upsell);
        selected = nextSelected;
      };

      const observer = new MutationObserver(sync);
      observer.observe(upsell, { attributes: true, attributeFilter: ['data-selected'] });
      upsell.dataset.lcA11yInitialised = 'true';
      sync();
    });
  }

  function initialiseJudgeMeTestimonialSlot(section) {
    const slot = section.querySelector('[data-lc-judgeme-testimonial-slot]');
    if (!slot || slot.dataset.initialised === 'true') return;

    const featured = slot.querySelector('[data-lc-judgeme-featured-review]');
    const stars = featured?.querySelector('[data-lc-review-stars]');
    const title = featured?.querySelector('[data-lc-review-title]');
    const body = featured?.querySelector('[data-lc-review-body]');
    const author = featured?.querySelector('[data-lc-review-author]');
    const verified = featured?.querySelector('[data-lc-review-verified]');

    const cleanText = (value) => {
      if (value == null) return '';
      const parsed = new DOMParser().parseFromString(String(value), 'text/html');
      return (parsed.body.textContent || '').replace(/\s+/g, ' ').trim();
    };

    const renderStars = (rating, sourceStars) => {
      if (!stars) return false;
      const score = Math.max(0, Math.min(5, Number(rating) || 0));
      if (score <= 0) return false;

      stars.replaceChildren();
      if (sourceStars) {
        stars.append(sourceStars.cloneNode(true));
      } else {
        const fragment = document.createDocumentFragment();
        for (let index = 1; index <= 5; index += 1) {
          const star = document.createElement('span');
          star.className = `jdgm-star ${index <= Math.round(score) ? 'jdgm--on' : 'jdgm--off'}`;
          star.setAttribute('aria-hidden', 'true');
          fragment.append(star);
        }
        stars.append(fragment);
      }
      stars.setAttribute('aria-label', `${score.toFixed(1)} out of 5 stars`);
      return true;
    };

    const renderReview = ({ rating, sourceStars, reviewTitle, reviewBody, reviewer, verifiedLabel }) => {
      const bodyText = cleanText(reviewBody);
      if (!featured || !body || !bodyText || !renderStars(rating, sourceStars)) return false;

      const titleText = cleanText(reviewTitle);
      const authorText = cleanText(reviewer);
      const verifiedText = cleanText(verifiedLabel);

      if (title) {
        title.textContent = titleText;
        title.hidden = titleText === '';
      }
      body.textContent = bodyText;
      if (author) {
        author.textContent = authorText;
        author.hidden = authorText === '';
      }
      if (verified) {
        verified.textContent = verifiedText;
        verified.hidden = verifiedText === '';
      }

      featured.hidden = false;
      slot.hidden = false;
      slot.dataset.reviewSource = 'judgeme';
      slot.dataset.reviewReady = 'true';
      return true;
    };

    const renderFromWidgetData = () => {
      const productId = slot.dataset.productId;
      const widget = window.jdgm?.data?.reviewWidget?.[productId];
      const reviews = Array.isArray(widget?.reviews) ? widget.reviews : [];
      const review = reviews.find((item) => cleanText(item?.body || item?.content || item?.review_body) !== '');
      if (!review) return false;

      const reviewer = review.reviewer || {};
      const isVerified =
        review.verified_buyer === true ||
        review.verified_buyer === 'true' ||
        review.verified === true ||
        review.verified === 'true' ||
        reviewer.verified_buyer === true;

      return renderReview({
        rating: review.rating || review.score,
        reviewTitle: review.title || review.review_title,
        reviewBody: review.body || review.content || review.review_body,
        reviewer: reviewer.name || review.reviewer_name || review.author,
        verifiedLabel: isVerified ? 'Verified Buyer' : '',
      });
    };

    const renderFromReviewWidget = () => {
      const reviews = Array.from(section.querySelectorAll('#judgeme_product_reviews .jdgm-rev'));
      const review = reviews.find((item) => cleanText(item.querySelector('.jdgm-rev__body')?.textContent) !== '');
      if (!review) return false;

      const sourceStars = review.querySelector('.jdgm-rev__rating, .jdgm-rev__stars, [data-score]');
      const verifiedBadge = review.querySelector('.jdgm-rev__buyer-badge');
      return renderReview({
        rating: sourceStars?.dataset.score || review.dataset.rating,
        sourceStars,
        reviewTitle: review.querySelector('.jdgm-rev__title')?.textContent,
        reviewBody: review.querySelector('.jdgm-rev__body')?.textContent,
        reviewer: review.querySelector('.jdgm-rev__author')?.textContent,
        verifiedLabel: verifiedBadge?.textContent,
      });
    };

    const placeOfficialWidget = () => {
      const candidate = Array.from(
        section.querySelectorAll(
          '.jdgm-carousel-wrapper, .jdgm-testimonials-carousel, [data-judgeme-testimonials-carousel]'
        )
      ).find(
        (widget) =>
          !slot.contains(widget) &&
          !widget.closest('.lc-judgeme-rating') &&
          !widget.closest('#judgeme_product_reviews')
      );
      if (candidate) {
        const appBlock = candidate.closest('.shopify-block') || candidate;
        slot.hidden = false;
        slot.dataset.reviewSource = 'judgeme-carousel';
        slot.dataset.reviewReady = 'true';
        slot.replaceChildren(appBlock);
        return;
      }

      if (slot.dataset.reviewReady === 'true') return;
      renderFromReviewWidget() || renderFromWidgetData();
    };

    const observer = new MutationObserver(placeOfficialWidget);
    observer.observe(section, { childList: true, subtree: true });
    const handleUnload = (event) => {
      if (!event.target.contains(slot)) return;
      observer.disconnect();
      document.removeEventListener('shopify:section:unload', handleUnload);
    };
    document.addEventListener('shopify:section:unload', handleUnload);
    slot.dataset.initialised = 'true';
    placeOfficialWidget();
  }

  function initialiseJudgeMeRating(section) {
    const badge = section.querySelector('[data-lc-judgeme-rating-link]');
    const reviews = section.querySelector('#judgeme_product_reviews');
    if (!badge || !reviews || badge.dataset.initialised === 'true') return;

    const preview = badge.querySelector('.jdgm-prev-badge');
    const hasReviews = integer(preview?.dataset.numberOfReviews) > 0;
    badge.dataset.judgemeEmpty = hasReviews ? 'false' : 'true';

    if (!badge.querySelector('a, button')) {
      badge.tabIndex = 0;
      badge.setAttribute('role', 'link');
    }

    const openReviews = (event) => {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      reviews.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    };

    badge.addEventListener('click', openReviews);
    badge.addEventListener('keydown', openReviews);
    badge.dataset.initialised = 'true';
  }

  function initialiseShippingUnlock(section) {
    const card = section.querySelector('[data-lc-shipping-card]');
    if (!card || card.dataset.initialised === 'true') return;

    const title = card.querySelector('[data-lc-shipping-title]');
    const status = card.querySelector('[data-lc-shipping-status]');
    const live = card.querySelector('[data-lc-shipping-live]');
    const threshold = integer(card.dataset.thresholdCents);
    const eligibleMarket = card.dataset.countryCode === 'US';
    const defaultTitle = card.dataset.defaultTitle || 'FREE SHIPPING';
    const defaultText = card.dataset.defaultText || 'US orders over $30';
    let unlocked = false;

    const render = () => {
      const projectedTotal =
        cartSubtotalCents(card) + selectedTierTotalCents(section) + selectedUpsellTotalCents(section);
      const nextUnlocked = eligibleMarket && projectedTotal >= threshold;
      card.dataset.projectedSubtotalCents = String(projectedTotal);
      card.classList.toggle('is-unlocked', nextUnlocked);

      if (nextUnlocked) {
        title.textContent = 'FREE SHIPPING UNLOCKED';
        status.textContent = 'Applied to this US order';
      } else {
        title.textContent = defaultTitle;
        status.textContent = defaultText;
      }

      if (nextUnlocked && !unlocked) {
        pulse(card, 'lc-unlock-pulse', 2350);
        if (live) live.textContent = 'Free shipping unlocked';
      } else if (!nextUnlocked && unlocked && live) {
        live.textContent = '';
      }
      unlocked = nextUnlocked;
    };

    const stateObserver = new MutationObserver(render);
    const quantityBreaks = section.querySelector('quantity-breaks');
    if (quantityBreaks) {
      stateObserver.observe(quantityBreaks, {
        subtree: true,
        attributes: true,
        attributeFilter: ['checked', 'data-base-price', 'data-price', 'class', 'data-selected-id'],
      });
      quantityBreaks.addEventListener('change', render);
    }
    section.querySelectorAll('.lc-mani-upsell').forEach((upsell) => {
      stateObserver.observe(upsell, {
        attributes: true,
        attributeFilter: ['data-selected', 'data-price', 'data-id'],
      });
    });

    const controller = new AbortController();
    const subscriptions = [];
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      subscriptions.push(
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => requestAnimationFrame(render))
      );
      subscriptions.push(
        subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
          if (!data?.sectionId || data.sectionId === section.dataset.section) {
            requestAnimationFrame(render);
          }
        })
      );
    }
    let cartObserver;
    document.addEventListener(
      'shopify:section:unload',
      (event) => {
        if (event.target.contains(card)) {
          controller.abort();
          stateObserver.disconnect();
          cartObserver?.disconnect();
          subscriptions.forEach((unsubscribe) => unsubscribe?.());
        }
      },
      { signal: controller.signal }
    );

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      let refreshTimer;
      cartObserver = new MutationObserver(() => {
        window.clearTimeout(refreshTimer);
        refreshTimer = window.setTimeout(render, 120);
      });
      cartObserver.observe(cartDrawer, { childList: true, subtree: true });
    }

    card.dataset.initialised = 'true';
    render();
  }

  function initialiseCampaignAccessibility(section) {
    const timer = section.querySelector('date-countdown-timer');
    if (!timer || timer.dataset.lcA11yInitialised === 'true') return;
    timer.setAttribute('aria-live', 'off');
    timer.querySelectorAll('.countdown-timer__number').forEach((number) => {
      number.setAttribute('aria-hidden', 'true');
      number.removeAttribute('aria-live');
    });
    timer.dataset.lcA11yInitialised = 'true';
  }

  function initialiseDetailsLink(section) {
    const link = section.querySelector('[data-lc-details-link]');
    const accordion = section.querySelector('.lc-product-accordion');
    if (!link || !accordion || link.dataset.initialised === 'true') return;

    if (accordion.id) link.setAttribute('href', `#${accordion.id}`);
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const details = accordion.querySelector('details');
      const summary = details?.querySelector('summary');
      if (details && !details.open && summary) summary.click();
      accordion.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
    });
    link.dataset.initialised = 'true';
  }

  function initialiseAccordionState(section) {
    section.querySelectorAll('.lc-product-accordion details').forEach((details) => {
      if (details.dataset.lcInitialised === 'true') return;
      const summary = details.querySelector('summary');
      const sync = () => summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      details.addEventListener('toggle', sync);
      details.dataset.lcInitialised = 'true';
      sync();
    });
  }

  function initialise(root = document) {
    const sections = [];
    if (root.matches?.(rootSelector)) sections.push(root);
    root.querySelectorAll?.(rootSelector).forEach((section) => sections.push(section));
    sections.forEach((section) => {
      initialiseTierStepper(section);
      initialiseDetailsLink(section);
      initialiseAccordionState(section);
      initialiseUpsellAccessibility(section);
      initialiseJudgeMeTestimonialSlot(section);
      initialiseJudgeMeRating(section);
      initialiseShippingUnlock(section);
      initialiseCampaignAccessibility(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
